import React from 'react';
import { View } from 'react-native';
import { formatDateShort, formatTimeOnly } from '../../helpers/formatDateTime.helper';
import {
  getSnapshotMarketsForMatch,
  shouldShowMatchWinner1x2BetRow,
} from '../../src/challenges/helpers/acceptChallengePicks.helper';
import type { ChallengeOddsSnapshot, ChallengePick } from '../../types/challenges';
import type { ChallengeCardStyles } from '../../src/challenges/styles/challengeCard.styles';
import {
  MATCH_WINNER_MARKET_ID,
  MATCH_WINNER_MARKET_NAME,
  PICK_OUTCOME_LABELS,
} from '../../src/challenges/constants/challenges.constants';
import { formatHandicapMarketDisplayName } from '../../src/create/constants/matchDetails.constants';
import { getTeamKitConfig } from '../../helpers/teamKit.helper';
import { MatchBetThreeColumnGrid } from './MatchBetThreeColumnGrid';
import { MatchBetThreeColumnMarketRow } from './MatchBetThreeColumnMarketRow';
import { getMatchDisplayStatusUpper } from '../../src/challenges/helpers/matchDisplayStatus.helper';

export type OtherPickDisplay = Pick<
  ChallengePick,
  'matchId' | 'userId' | 'marketId' | 'marketName' | 'value' | 'odd' | 'isCorrectOutcome'
>;

export interface Match {
  id: string;
  date: string | Date;
  status?: string;
  homeTeam?: { name: string } | string;
  awayTeam?: { name: string } | string;
  league?: { name: string } | string;
  odds?: {
    home?: number;
    homeWin?: number;
    draw?: number;
    drawOdds?: number;
    away?: number;
    awayWin?: number;
  };
  homeOdds?: number;
  drawOdds?: number;
  awayOdds?: number;
  homeScore?: number;
  awayScore?: number;
}

interface ChallengeMatchRowProps {
  match: Match;
  styles: ChallengeCardStyles;
  pick: 'home' | 'draw' | 'away' | null;
  otherPicksForMatch: OtherPickDisplay[];
  otherParticipantPicksForMatch: OtherPickDisplay[];
  opponent1x2: { value: 'home' | 'draw' | 'away'; odd: number; isCorrect?: boolean } | null | undefined;
  my1x2Odd: number | undefined;
  my1x2Correct: boolean | undefined;
  showRequiredMarketsOnly: boolean;
  requiredMarkets: Array<{ marketId: number; marketName: string }>;
  challengeHasOddsSnapshot: boolean;
  oddsSnapshot: ChallengeOddsSnapshot | null;
  useConstrainedCard: boolean;
  onOpenPicksModal?: () => void;
  testID?: string;
}

export const ChallengeMatchRow: React.FC<ChallengeMatchRowProps> = ({
  match,
  styles,
  pick,
  otherPicksForMatch,
  otherParticipantPicksForMatch,
  opponent1x2,
  my1x2Odd,
  my1x2Correct,
  showRequiredMarketsOnly,
  requiredMarkets,
  challengeHasOddsSnapshot,
  oddsSnapshot,
  useConstrainedCard,
  onOpenPicksModal,
  testID,
}) => {
  const matchOdds = match.odds || {};
  const homeOdds = matchOdds.home ?? matchOdds.homeWin ?? match.homeOdds ?? 2.0;
  const drawOdds = matchOdds.draw ?? matchOdds.drawOdds ?? match.drawOdds ?? 3.0;
  const awayOdds = matchOdds.away ?? matchOdds.awayWin ?? match.awayOdds ?? 2.0;

  const homeTeamName =
    typeof match.homeTeam === 'string'
      ? match.homeTeam
      : (match.homeTeam as { name?: string })?.name ?? 'Home';
  const awayTeamName =
    typeof match.awayTeam === 'string'
      ? match.awayTeam
      : (match.awayTeam as { name?: string })?.name ?? 'Away';
  const homeTeamSource = typeof match.homeTeam === 'string' ? match.homeTeam : match.homeTeam;
  const awayTeamSource = typeof match.awayTeam === 'string' ? match.awayTeam : match.awayTeam;
  const homeKitConfig = getTeamKitConfig(homeTeamSource ?? homeTeamName);
  const awayKitConfig = getTeamKitConfig(awayTeamSource ?? awayTeamName);

  const showAsInviteePreview = showRequiredMarketsOnly && requiredMarkets.length > 0;
  const displayMatchStatusUpper = getMatchDisplayStatusUpper(match.status, match.date);
  const showOpponentCorrectWrong =
    showAsInviteePreview ||
    displayMatchStatusUpper === 'LIVE' ||
    displayMatchStatusUpper === 'IN_PLAY' ||
    displayMatchStatusUpper === 'FINISHED';

  const matchDatePart = formatDateShort(match.date);
  const matchTimePart = formatTimeOnly(match.date);
  const isLive = displayMatchStatusUpper === 'LIVE' || displayMatchStatusUpper === 'IN_PLAY';
  const hasActualScore = typeof match.homeScore === 'number' && typeof match.awayScore === 'number';
  const hasScore = isLive || (hasActualScore && displayMatchStatusUpper === 'FINISHED');
  const displayHomeScore = hasActualScore ? match.homeScore : isLive ? 0 : undefined;
  const displayAwayScore = hasActualScore ? match.awayScore : isLive ? 0 : undefined;
  const liveCaption = !hasScore && isLive ? 'LIVE' : undefined;

  const my1x2Label = pick != null ? PICK_OUTCOME_LABELS[pick] : undefined;
  const useMyPickOdd = my1x2Odd != null && Number.isFinite(my1x2Odd) && my1x2Odd > 1;
  const my1x2Odds = useMyPickOdd
    ? my1x2Odd
    : pick === 'home'
      ? homeOdds
      : pick === 'draw'
        ? drawOdds
        : pick === 'away'
          ? awayOdds
          : undefined;
  // Always show what the opponent put when they have a pick (no ? for opponent)
  const opponent1x2Label = opponent1x2 ? PICK_OUTCOME_LABELS[opponent1x2.value] : undefined;
  const opponent1x2Odds =
    opponent1x2
      ? opponent1x2.odd != null && opponent1x2.odd > 1
        ? opponent1x2.odd
        : opponent1x2.value === 'home'
          ? homeOdds
          : opponent1x2.value === 'draw'
            ? drawOdds
            : awayOdds
      : undefined;
  const opponent1x2Correct = showOpponentCorrectWrong && opponent1x2 ? opponent1x2.isCorrect : undefined;
  const my1x2Wrong = pick != null && my1x2Correct === false;
  const opponent1x2Wrong =
    showOpponentCorrectWrong && opponent1x2 && opponent1x2.isCorrect === false;

  const hasRequired1x2 = requiredMarkets.some((rm) => rm.marketId === MATCH_WINNER_MARKET_ID);
  const snapshotMarkets = getSnapshotMarketsForMatch(oddsSnapshot, match.id);
  const hasUserStored1x2 =
    pick != null || (my1x2Odd != null && Number.isFinite(my1x2Odd) && my1x2Odd > 1);
  const hasOpponentStored1x2 = opponent1x2 != null;
  const fromPicksOrRequired = hasRequired1x2 || hasUserStored1x2 || hasOpponentStored1x2;
  const shouldShow1x2BetRow =
    fromPicksOrRequired ||
    (!showRequiredMarketsOnly &&
      shouldShowMatchWinner1x2BetRow({
        snapshotMarkets,
        challengeHasOddsSnapshot,
        hasRequiredMatchWinner: false,
        hasUserStored1x2: false,
        hasOpponentStored1x2: false,
      }));

  const getMyOtherPick = (marketId: number) =>
    otherPicksForMatch.find((p) => p.marketId === marketId);
  const getOpponentOtherPick = (marketId: number) =>
    otherParticipantPicksForMatch.find((p) => p.marketId === marketId);

  const cardContent = (
    <>
      <MatchBetThreeColumnGrid
        styles={styles}
        homeTeamName={homeTeamName}
        awayTeamName={awayTeamName}
        homeKit={{
          primary: homeKitConfig.primary,
          secondary: homeKitConfig.secondary,
          type: homeKitConfig.type,
        }}
        awayKit={{
          primary: awayKitConfig.primary,
          secondary: awayKitConfig.secondary,
          type: awayKitConfig.type,
        }}
        kitSize={28}
        hasScore={hasScore}
        homeScore={displayHomeScore}
        awayScore={displayAwayScore}
        matchDatePart={matchDatePart}
        matchTimePart={matchTimePart}
        liveCaption={liveCaption}
        isLive={isLive}
        showBetRow={shouldShow1x2BetRow}
        centerMarketLabel={MATCH_WINNER_MARKET_NAME}
        myPick={{
          label: my1x2Label,
          odds: my1x2Odds,
          isCorrect: my1x2Correct ?? undefined,
          isWrong: my1x2Wrong ?? false,
          onPress:
            showRequiredMarketsOnly && onOpenPicksModal && my1x2Label == null
              ? onOpenPicksModal
              : undefined,
          testID: testID ? `${testID}-my-1x2` : undefined,
        }}
        opponentPick={{
          label: opponent1x2Label,
          odds: opponent1x2Odds,
          isCorrect: opponent1x2Correct ?? undefined,
          isWrong: opponent1x2Wrong ?? false,
          testID: testID ? `${testID}-opponent-1x2` : undefined,
        }}
      />

      {requiredMarkets.filter((rm) => rm.marketId !== MATCH_WINNER_MARKET_ID).length > 0 ? (
        <View style={styles.marketRowsContainer}>
          {requiredMarkets
            .filter((rm) => rm.marketId !== MATCH_WINNER_MARKET_ID)
            .map((rm, idx) => {
              const myPick = getMyOtherPick(rm.marketId);
              const opponentPick = getOpponentOtherPick(rm.marketId);
              const handicapDisplayName = formatHandicapMarketDisplayName(
                rm.marketId,
                rm.marketName,
                myPick?.value ?? opponentPick?.value
              );
              return (
                <MatchBetThreeColumnMarketRow
                  key={`${match.id}-${rm.marketId}-${idx}`}
                  styles={styles}
                  marketNameText={handicapDisplayName}
                  myPick={{
                    label: myPick?.value,
                    odds: myPick?.odd,
                    isCorrect: myPick?.isCorrectOutcome ?? undefined,
                    isWrong: myPick?.isCorrectOutcome === false,
                    onPress:
                      showRequiredMarketsOnly && onOpenPicksModal && myPick == null
                        ? onOpenPicksModal
                        : undefined,
                    testID: testID ? `${testID}-my-${rm.marketId}` : undefined,
                  }}
                  opponentPick={{
                    label: opponentPick?.value,
                    odds: opponentPick?.odd,
                    isCorrect: opponentPick?.isCorrectOutcome ?? undefined,
                    isWrong: opponentPick?.isCorrectOutcome === false,
                    testID: testID ? `${testID}-opponent-${rm.marketId}` : undefined,
                  }}
                />
              );
            })}
        </View>
      ) : null}
    </>
  );

  return (
    <View style={styles.picksCard}>
      {useConstrainedCard ? (
        <View style={styles.picksCardInner}>{cardContent}</View>
      ) : (
        cardContent
      )}
    </View>
  );
};
